import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sales-users`;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });
export const repPhotoUrl = (token) => (token ? `${process.env.REACT_APP_BACKEND_URL}/api/sales/rep-photo/${token}.jpg` : null);
const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

export const RepAvatar = ({ user, size = 'h-10 w-10' }) => {
  const url = repPhotoUrl(user?.photo_token);
  return url
    ? <img src={url} alt="" className={`${size} rounded-full object-cover border-2 border-amber-300 shrink-0`} data-testid={`sales-user-avatar-${user.username}`} />
    : <span className={`${size} rounded-full bg-gradient-to-br from-orange-400 to-amber-500 text-white text-xs font-bold flex items-center justify-center shrink-0`} data-testid={`sales-user-avatar-${user?.username}`}>{initials(user?.full_name)}</span>;
};

const L = {
  'pt-BR': { title: 'Foto profissional (IA)', hint: 'Envie uma foto do vendedor sorrindo — a IA gera o retrato profissional (rosto igual, roupa e fundo profissionais). Ela aparece no e-mail de confirmação da visita ao cliente.', upload: 'Enviar foto', replace: 'Trocar foto', remove: 'Remover', generating: 'Gerando retrato profissional… (20-60s)', done: 'Foto profissional salva', removed: 'Foto removida', none: 'Sem foto — o vendedor será obrigado a enviar ao entrar no portal.' },
  en: { title: 'Professional photo (AI)', hint: 'Upload a smiling photo of the rep — AI generates the professional headshot (same face, professional outfit and background). It appears in the customer\'s appointment confirmation email.', upload: 'Upload photo', replace: 'Replace photo', remove: 'Remove', generating: 'Generating professional headshot… (20-60s)', done: 'Professional photo saved', removed: 'Photo removed', none: 'No photo — the rep will be required to upload one when they log in.' },
};

// Admin → Portal de Vendas → editar vendedor: foto profissional (gera e aplica na hora, sem aprovação)
export const SalesUserPhoto = ({ user, language = 'pt-BR', onChanged }) => {
  const t = L[language] || L['pt-BR'];
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/${user.id}/photo`, fd, { ...auth(), timeout: 200000 });
      toast.success(t.done);
      onChanged?.(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { const { data } = await axios.delete(`${API}/${user.id}/photo`, auth()); toast.success(t.removed); onChanged?.(data); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2" data-testid="sales-user-photo-section">
      <p className="text-xs font-semibold text-slate-800">{t.title}</p>
      <div className="flex items-center gap-3">
        <RepAvatar user={user} size="h-16 w-16" />
        <div className="flex-1 min-w-0 text-[11px] text-slate-600">{busy ? <span className="inline-flex items-center gap-1 font-semibold text-amber-800" data-testid="sales-user-photo-generating"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t.generating}</span> : (user.photo_token ? t.hint : t.none)}</div>
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" className="text-xs" disabled={busy} onClick={() => ref.current?.click()} data-testid="sales-user-photo-upload"><Camera className="h-3.5 w-3.5 mr-1" /> {user.photo_token ? t.replace : t.upload}</Button>
        {user.photo_token && <Button type="button" size="sm" variant="outline" className="text-xs text-red-600" disabled={busy} onClick={remove} data-testid="sales-user-photo-remove"><Trash2 className="h-3.5 w-3.5 mr-1" /> {t.remove}</Button>}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="sales-user-photo-input" />
      </div>
    </div>
  );
};
